import { useState, ReactElement, useEffect } from 'react';
import { useControl, Marker, MarkerProps, ControlPosition, useMap } from 'react-map-gl';
import MapboxGeocoder, { GeocoderOptions } from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

type GeocoderControlProps = Omit<GeocoderOptions, 'accessToken' | 'mapboxgl' | 'marker'> & {
  mapboxAccessToken: string;
  marker?: boolean | Omit<MarkerProps, 'longitude' | 'latitude'>;

  position: ControlPosition;

  bbox?: [number, number, number, number];
  proximity?: { longitude: number; latitude: number };

  onLoading?: (e: object) => void;
  onResults?: (e: object) => void;
  onResult?: (e: object) => void;
  onError?: (e: object) => void;
};

export default function GeocoderControl({
  mapboxAccessToken,
  marker = true,
  position,
  bbox,
  proximity,
  onLoading = () => {},
  onResults = () => {},
  onResult = () => {},
  onError = () => {},
  ...props
}: GeocoderControlProps) {
  const [markerElement, setMarker] = useState<ReactElement | null>(null);
  const { current: map } = useMap();

  const geocoder = useControl<MapboxGeocoder>(
    () => {
      const ctrl = new MapboxGeocoder({
        ...props,
        marker: false,
        bbox,
        proximity,
        accessToken: mapboxAccessToken,
        flyTo: {
          zoom: 16,
          speed: 4,
          bearing: 0,
          pitch: 50,
          easing: (t) => t,
        },
      });

      ctrl.on('loading', onLoading);
      ctrl.on('results', onResults);

      ctrl.on('result', (evt: any) => {
        onResult?.(evt);
        const location =
          evt?.result?.center ||
          (evt?.result?.geometry?.type === 'Point' && evt.result.geometry.coordinates);

        if (location && marker) {
          const markerProps = typeof marker === 'object' ? marker : {};
          setMarker(<Marker {...markerProps} longitude={location[0]} latitude={location[1]} />);
        } else {
          setMarker(null);
        }
      });

      ctrl.on('error', onError);
      return ctrl;
    },
    { position }
  );

  // Clear marker on map click
  useEffect(() => {
    if (!map) return;
    const clearMarker = () => setMarker(null);
    map.on('click', clearMarker);
    return () => {
      map.off('click', clearMarker);
    };
  }, [map]);

  // Dynamically update props if changed
  // @ts-ignore (TS2339 - private member usage)
  if (geocoder._map) {
    if (geocoder.getProximity() !== proximity && proximity !== undefined) {
      geocoder.setProximity(proximity);
    }
    if (geocoder.getRenderFunction() !== props.render && props.render !== undefined) {
      geocoder.setRenderFunction(props.render);
    }
    if (geocoder.getLanguage() !== props.language && props.language !== undefined) {
      geocoder.setLanguage(props.language);
    }
    if (geocoder.getZoom() !== props.zoom && props.zoom !== undefined) {
      geocoder.setZoom(props.zoom);
    }
    if (geocoder.getFlyTo() !== props.flyTo && props.flyTo !== undefined) {
      geocoder.setFlyTo(props.flyTo);
    }
    if (geocoder.getPlaceholder() !== props.placeholder && props.placeholder !== undefined) {
      geocoder.setPlaceholder(props.placeholder);
    }
    if (geocoder.getCountries() !== props.countries && props.countries !== undefined) {
      geocoder.setCountries(props.countries);
    }
    if (geocoder.getTypes() !== props.types && props.types !== undefined) {
      geocoder.setTypes(props.types);
    }
    if (geocoder.getMinLength() !== props.minLength && props.minLength !== undefined) {
      geocoder.setMinLength(props.minLength);
    }
    if (geocoder.getLimit() !== props.limit && props.limit !== undefined) {
      geocoder.setLimit(props.limit);
    }
    if (geocoder.getFilter() !== props.filter && props.filter !== undefined) {
      geocoder.setFilter(props.filter);
    }
    if (geocoder.getOrigin() !== props.origin && props.origin !== undefined) {
      geocoder.setOrigin(props.origin);
    }
  }

  return markerElement;
}
